import React from 'react';
import ReportCard from '../components/ReportCard';
import StackedShareBar from '../components/StackedShareBar';
import SegmentLegend from '../components/SegmentLegend';
import { AGE_SPREAD_NOTE, MIN_COHORT, SMALL_SAMPLE_NOTE } from '../copy';
import { collapseBuckets, fmtCount, fmtPct, share, shareAtLeast, tip } from '../format';
import { Mono } from '../components/Emphasis';

const LABELS = ['Zero', 'One', 'Two or more'];

const AgeRow = ({ band }) => {
  const total = band.total;
  const tooSmall = total > 0 && total < MIN_COHORT;
  const buckets = collapseBuckets(band.buckets, 2);
  const twoPlus = share(buckets[2].count, total);

  const segments = buckets.map((bucket, index) => ({
    key: bucket.flags,
    length: share(bucket.count, total),
    tip: tip(
      `Age ${band.band} · ${LABELS[index]}`,
      `${fmtCount(bucket.count)} employees · ${fmtPct(share(bucket.count, total))}`
    ),
  }));

  return (
    <div className='grid items-center gap-ds-4' style={{ gridTemplateColumns: '56px 1fr 150px' }}>
      <div className='font-jetbrains-mono text-[12px] font-semibold text-gray-800'>{band.band}</div>
      {total ? <StackedShareBar segments={segments} /> : <div className='h-[26px] rounded-ds-sm bg-gray-200' />}
      <div className='text-right text-ts-xs text-gray-600'>
        <span className='font-jetbrains-mono font-semibold text-gray-900'>{fmtCount(total)}</span>
        {total && !tooSmall ? ` · ${fmtPct(twoPlus)} at 2+` : ''}
        {tooSmall ? ` · ${SMALL_SAMPLE_NOTE}` : ''}
      </div>
    </div>
  );
};

/**
 * Deficiency burden by age band. Unlike the metabolic picture this one is flat, so the side panel states
 * the spread between the lightest and heaviest band rather than naming an inflection point.
 */
const DeficiencyByAge = ({ data }) => {
  const populated = data.by_age.filter((band) => band.total >= MIN_COHORT);
  const shares = populated.map((band) => shareAtLeast(band.buckets, 2));
  const low = shares.length ? Math.min(...shares) : 0;
  const high = shares.length ? Math.max(...shares) : 0;
  const flat = high - low <= 5;

  return (
    <div className='mt-ds-9 grid grid-cols-1 items-start gap-ds-5 lg:grid-cols-[1.5fr_1fr]'>
      <ReportCard fileName='deficiency-by-age-band' className='p-[26px]'>
        <h3 className='m-0 font-fig-extrabold text-[26px] font-bold leading-[1.18] text-gray-900'>
          {flat ? 'Age groups look broadly similar' : 'Burden varies across age bands'}
        </h3>
        <p className='mb-ds-5 mt-[6px] text-xs text-gray-600'>
          {flat
            ? 'Deficiency burden by age band — no single age group stands out'
            : 'Deficiency burden by age band'}
        </p>
        <SegmentLegend labels={LABELS} gap={18} />
        <div className='grid gap-[14px]'>
          {data.by_age.map((band) => (
            <AgeRow key={band.band} band={band} />
          ))}
        </div>
      </ReportCard>

      <div className='rounded-ds-lg border border-gray-300 bg-gray-100 p-[26px]'>
        <div className='mb-ds-4 text-ts-xs font-semibold uppercase tracking-[0.12em] text-gray-600'>
          Reading the bands
        </div>
        <p className='m-0 mb-[18px] text-[16px] leading-[1.7] text-gray-800' style={{ textWrap: 'pretty' }}>
          Every age band sits between <Mono>{fmtPct(low)}</Mono> and <Mono>{fmtPct(high)}</Mono> at two or more
          abnormalities — a spread of {flat ? 'just ' : ''}
          {(high - low).toFixed(1)} points.
        </p>
        {flat ? <p className='m-0 text-ts-md leading-[1.65] text-gray-700'>{AGE_SPREAD_NOTE}</p> : null}
      </div>
    </div>
  );
};

export default DeficiencyByAge;
